/**
 * RsvpDashboardPage — host view of RSVP responses for one invitation.
 * Route: /dashboard/invitations/:invitationId/rsvps
 *
 * Sprint 3 additions:
 *   - Analytics section (acceptance rate, response rate, forecast, time-series)
 *   - Server-side search / filter / sort
 *   - CSV and Excel export
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getRsvpSummary, getRsvpAnalytics, searchRsvps, exportRsvpsCsv, exportRsvpsExcel } from './api';
import type { Rsvp, RsvpSummary, RsvpAnalytics, RsvpSortField, PaginationMeta } from './types';
import { RSVP_STATUS_LABEL } from './types';

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function triggerDownload(blob: Blob | string, filename: string, mime: string) {
  const url = URL.createObjectURL(
    typeof blob === 'string' ? new Blob([blob], { type: mime }) : blob,
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const STATUS_COLOR: Record<string, string> = {
  ACCEPTED: 'text-green-600 bg-green-50 border-green-200',
  DECLINED: 'text-red-600 bg-red-50 border-red-200',
  MAYBE:    'text-yellow-600 bg-yellow-50 border-yellow-200',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 flex flex-col gap-1">
      <span className="text-xs font-medium text-on-surface-secondary uppercase tracking-wider">{label}</span>
      <span className="text-3xl font-bold text-on-surface tabular-nums">{value}</span>
      {sub && <span className="text-xs text-on-surface-secondary">{sub}</span>}
    </div>
  );
}

function RateCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 flex flex-col gap-2">
      <span className="text-xs font-medium text-on-surface-secondary uppercase tracking-wider">{label}</span>
      <div className="flex items-end gap-1">
        <span className={`text-3xl font-bold tabular-nums ${color}`}>{value}</span>
        <span className="text-sm text-on-surface-secondary mb-1">%</span>
      </div>
      <div className="h-1.5 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full ${color.replace('text-', 'bg-')}`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function RsvpDashboardPage() {
  const { invitationId = '' } = useParams<{ invitationId: string }>();

  const [rsvps, setRsvps]       = useState<Rsvp[]>([]);
  const [summary, setSummary]   = useState<RsvpSummary | null>(null);
  const [analytics, setAnalytics] = useState<RsvpAnalytics | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  // Search / filter / sort (server-side)
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [sort, setSort]               = useState<RsvpSortField>('latest');
  const [page, setPage]               = useState(1);
  const [meta, setMeta]               = useState<PaginationMeta | null>(null);
  const [searching, setSearching]     = useState(false);

  // Export
  const [exporting, setExporting] = useState<'csv' | 'excel' | null>(null);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rsvpSummary, rsvpAnalytics] = await Promise.all([
        getRsvpSummary(invitationId),
        getRsvpAnalytics(invitationId),
      ]);
      setSummary(rsvpSummary);
      setAnalytics(rsvpAnalytics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load RSVP data.');
    } finally {
      setLoading(false);
    }
  }, [invitationId]);

  const loadFiltered = useCallback(async (q: { page: number; search?: string; status?: string; sort?: RsvpSortField }) => {
    setSearching(true);
    try {
      const res = await searchRsvps(invitationId, {
        page: q.page,
        limit: 25,
        search: q.search,
        status: q.status,
        sort: q.sort,
      });
      setRsvps(res.items);
      setMeta(res.meta);
    } catch {
      // silently keep previous results on filter error
    } finally {
      setSearching(false);
    }
  }, [invitationId]);

  useEffect(() => { void loadInitial(); }, [loadInitial]);

  // Debounce search input
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === search) return;
    const t = setTimeout(() => {
      setSearch(trimmed);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  // Fire server search on filter/sort/page change
  useEffect(() => {
    void loadFiltered({
      page,
      search: search || undefined,
      status: statusFilter,
      sort,
    });
  }, [loadFiltered, page, search, statusFilter, sort]);

  const handleExportCsv = async () => {
    setExporting('csv');
    try {
      const csv = await exportRsvpsCsv(invitationId);
      triggerDownload(csv, `rsvps-${invitationId}.csv`, 'text/csv;charset=utf-8;');
    } catch {
      // ignore — user can retry
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = async () => {
    setExporting('excel');
    try {
      const blob = await exportRsvpsExcel(invitationId);
      triggerDownload(blob, `rsvps-${invitationId}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch {
      // ignore — user can retry
    } finally {
      setExporting(null);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-on-surface-secondary text-sm">
        Loading RSVPs…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <p className="text-status-failed font-medium mb-4">{error}</p>
        <button
          onClick={() => void loadInitial()}
          className="px-5 py-2 rounded-full bg-primary text-white text-sm font-medium hover:opacity-90 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 flex flex-col gap-8">

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Link
          to="/dashboard/invitations"
          className="text-on-surface-secondary hover:text-on-surface text-sm flex items-center gap-1 transition-colors"
        >
          ← Invitations
        </Link>
        <span className="text-on-surface-secondary">/</span>
        <h1 className="text-xl font-bold text-on-surface">RSVP Responses</h1>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => void handleExportCsv()}
            disabled={exporting !== null}
            className="text-xs px-3 py-1.5 rounded-lg border border-border bg-surface text-on-surface hover:bg-surface-secondary transition disabled:opacity-50"
          >
            {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            onClick={() => void handleExportExcel()}
            disabled={exporting !== null}
            className="text-xs px-3 py-1.5 rounded-lg border border-border bg-surface text-on-surface hover:bg-surface-secondary transition disabled:opacity-50"
          >
            {exporting === 'excel' ? 'Exporting…' : 'Export Excel'}
          </button>
          <button
            onClick={() => void loadInitial()}
            className="text-xs text-primary hover:underline"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <SummaryCard label="Total Responses" value={summary.total} />
          <SummaryCard label="Total Attendees" value={summary.totalHeadcount} sub="confirmed (accepted)" />
          <SummaryCard label="Accepted" value={summary.byStatus.ACCEPTED.count} sub={`${summary.byStatus.ACCEPTED.headcount} attending`} />
          <SummaryCard label="Declined" value={summary.byStatus.DECLINED.count} />
          <SummaryCard label="Maybe" value={summary.byStatus.MAYBE.count} sub={`${summary.byStatus.MAYBE.headcount} possible`} />
        </div>
      )}

      {/* Analytics section */}
      {analytics && analytics.total > 0 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-base font-semibold text-on-surface">Response Analytics</h2>

          {/* Rate cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <RateCard label="Acceptance Rate" value={analytics.acceptanceRate} color="text-green-600" />
            <RateCard label="Decline Rate"    value={analytics.declineRate}    color="text-red-500" />
            <RateCard label="Response Rate"   value={analytics.responseRate}   color="text-blue-600" />
            <RateCard label="Maybe Rate"      value={analytics.pendingRate}    color="text-yellow-600" />
          </div>

          {/* Attendance forecast */}
          <div className="rounded-2xl border border-border bg-surface px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-medium text-on-surface-secondary uppercase tracking-wider">Attendance Forecast</p>
              <p className="text-2xl font-bold text-on-surface tabular-nums mt-1">
                ~{analytics.attendanceForecast} guests
              </p>
            </div>
            <p className="text-xs text-on-surface-secondary max-w-xs text-right">
              Confirmed + 50% of maybe responses. Subject to change.
            </p>
          </div>

          {/* Time-series chart (simple bar) */}
          {analytics.timeSeries.length > 1 && (
            <div className="rounded-2xl border border-border bg-surface p-4">
              <p className="text-xs font-medium text-on-surface-secondary uppercase tracking-wider mb-3">Responses Over Time</p>
              <ResponseTimeSeries data={analytics.timeSeries} />
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex-1 rounded-xl border border-border bg-surface px-4 py-2 text-sm text-on-surface placeholder:text-on-surface-secondary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-xl border border-border bg-surface px-4 py-2 text-sm text-on-surface outline-none focus:border-primary transition"
        >
          <option value="ALL">All statuses</option>
          <option value="ACCEPTED">Accepted</option>
          <option value="DECLINED">Declined</option>
          <option value="MAYBE">Maybe</option>
        </select>
        <select
          value={sort}
          onChange={(e) => { setSort(e.target.value as RsvpSortField); setPage(1); }}
          className="rounded-xl border border-border bg-surface px-4 py-2 text-sm text-on-surface outline-none focus:border-primary transition"
        >
          <option value="latest">Latest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option>
          <option value="headcount">Highest headcount</option>
        </select>
      </div>

      {/* Guest table */}
      {searching ? (
        <div className="text-center py-8 text-on-surface-secondary text-sm">Searching…</div>
      ) : rsvps.length === 0 ? (
        <div className="text-center py-16 text-on-surface-secondary text-sm">
          {!search && statusFilter === 'ALL'
            ? 'No RSVPs yet. Share your invitation to start collecting responses.'
            : 'No responses match your filters.'}
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          {/* Mobile: cards */}
          <div className="sm:hidden divide-y divide-border">
            {rsvps.map((r) => (
              <div key={r.id} className="p-4 flex flex-col gap-2 bg-surface">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-on-surface text-sm">{r.guestName}</p>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLOR[r.status] ?? ''}`}>
                    {RSVP_STATUS_LABEL[r.status as keyof typeof RSVP_STATUS_LABEL] ?? r.status}
                  </span>
                </div>
                {r.guestEmail && <p className="text-xs text-on-surface-secondary">{r.guestEmail}</p>}
                <div className="flex items-center gap-4 text-xs text-on-surface-secondary">
                  <span>{r.status !== 'DECLINED' ? `${r.headcount} guest${r.headcount !== 1 ? 's' : ''}` : '—'}</span>
                  <span>{formatDate(r.respondedAt)}</span>
                </div>
                {r.message && (
                  <p className="text-xs text-on-surface-secondary italic border-l-2 border-border pl-2">
                    {r.message}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <table className="hidden sm:table w-full text-sm">
            <thead>
              <tr className="bg-surface-secondary border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-on-surface-secondary">Guest</th>
                <th className="text-left px-4 py-3 font-medium text-on-surface-secondary">Status</th>
                <th className="text-right px-4 py-3 font-medium text-on-surface-secondary">Guests</th>
                <th className="text-left px-4 py-3 font-medium text-on-surface-secondary">Submitted</th>
                <th className="text-left px-4 py-3 font-medium text-on-surface-secondary">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface">
              {rsvps.map((r) => (
                <tr key={r.id} className="hover:bg-surface-secondary/50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-on-surface">{r.guestName}</p>
                    {r.guestEmail && <p className="text-xs text-on-surface-secondary">{r.guestEmail}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_COLOR[r.status] ?? ''}`}>
                      {RSVP_STATUS_LABEL[r.status as keyof typeof RSVP_STATUS_LABEL] ?? r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-on-surface tabular-nums">
                    {r.status !== 'DECLINED' ? r.headcount : '—'}
                  </td>
                  <td className="px-4 py-3 text-on-surface-secondary text-xs">{formatDate(r.respondedAt)}</td>
                  <td className="px-4 py-3 text-on-surface-secondary text-xs max-w-xs truncate">{r.message ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <p className="text-xs text-on-surface-secondary">
          {meta?.total ?? 0} response{(meta?.total ?? 0) !== 1 ? 's' : ''}
          {statusFilter !== 'ALL' || search ? ' (filtered)' : ''}
          {meta && meta.totalPages > 1 && ` · page ${meta.page} of ${meta.totalPages}`}
        </p>
        {meta && meta.totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={searching || meta.page <= 1}
              className="px-3 py-1.5 rounded-lg border border-border text-xs text-on-surface disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-secondary transition"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={searching || meta.page >= meta.totalPages}
              className="px-3 py-1.5 rounded-lg border border-border text-xs text-on-surface disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-secondary transition"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline time-series bar chart ──────────────────────────────────────────────

function ResponseTimeSeries({ data }: { data: Array<{ date: string; count: number }> }) {
  const max = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="flex items-end gap-1 h-20 overflow-x-auto pb-1">
      {data.map((d) => (
        <div key={d.date} className="flex flex-col items-center gap-1 flex-shrink-0" style={{ minWidth: 24 }}>
          <div
            title={`${d.date}: ${d.count}`}
            className="w-5 rounded-t bg-primary/60 hover:bg-primary transition-colors cursor-default"
            style={{ height: `${Math.max((d.count / max) * 64, 4)}px` }}
          />
          <span className="text-[9px] text-on-surface-secondary" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {d.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
}
